import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Loader2 } from "lucide-react";
import { useFolders } from "@/hooks/use-folders";

interface ClaimsFolderBarProps {
  selectedFolderId: number | "all";
  onSelect: (id: number | "all") => void;
  asSubmenu?: boolean;
}

/**
 * Compact folder controls for the Claims page. Folder filtering uses a single
 * dropdown selector. Folder creation and management live in Settings.
 */
export function ClaimsFolderBar({
  selectedFolderId,
  onSelect,
  asSubmenu = false,
}: ClaimsFolderBarProps) {
  const {
    folders,
    isLoading,
  } = useFolders();

  const selectedFolder =
    selectedFolderId === "all"
      ? null
      : folders.find((folder) => folder.id === selectedFolderId) ?? null;
  const selectedLabel = selectedFolder
    ? selectedFolder.name
    : "All Folders";

  const folderMenu = isLoading ? (
    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading folders...
    </div>
  ) : (
    <>
      <DropdownMenuRadioGroup
        value={selectedFolderId === "all" ? "all" : String(selectedFolderId)}
        onValueChange={(value) => onSelect(value === "all" ? "all" : Number(value))}
      >
        <DropdownMenuRadioItem value="all" onSelect={(event) => { if (asSubmenu) event.preventDefault(); }}>All Folders</DropdownMenuRadioItem>
        {folders.map((folder) => (
          <DropdownMenuRadioItem key={folder.id} value={String(folder.id)} onSelect={(event) => { if (asSubmenu) event.preventDefault(); }}>
            <span className="inline-flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full border"
                style={{ backgroundColor: folder.color ?? "#94A3B8" }}
                aria-hidden="true"
              />
              <span className="truncate">{folder.name}</span>
            </span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  );

  if (asSubmenu) {
    return (
      <>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Folder</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-[230px] max-h-[calc(100dvh-2rem)] overflow-y-auto">
            {folderMenu}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-2 max-sm:w-full">
      <div className="flex min-w-0 items-center gap-2 max-sm:flex-1">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading folders...
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-[190px] justify-between max-sm:w-full">
                <span className="truncate">{selectedLabel}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[230px] max-sm:w-[var(--radix-dropdown-menu-trigger-width)]">
              {folderMenu}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
